import { useGlobalModal } from '@/hooks/useGlobalModal';
import { AU_BACKDROP_SX, AU_DIALOG_PAPER_SX } from '@/theme/au-compat';
import GlobalModal from '@components/GlobalModal';
import DeviceNameSpan from '@components/deviceNameSpan';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access } from '@features/authorization';
import { MovementDataRenderer } from '@features/gateCommon';
import { Trans } from '@lingui/macro';
import { Box, Skeleton } from '@mui/material';
import { type FC } from 'react';
import { ConnectionStatus } from './ConnectionStatus';
import { GateOperationSubscription } from './GateOperationSubscription';
import { PanelChart } from './PanelChart';
import { PanelNotifications } from './PanelNotifications';
import { PanelOperation } from './PanelOperation';
import { PanelSummary } from './PanelSummary';

/**
 * El chrome del diálogo, declarado una vez, igual que en `moveGateModal`. `maxWidth: false` con un
 * ancho explícito porque los breakpoints de MUI no son el eje de ancho de `.au-dialog`: el
 * espécimen tiene `md` 460, `lg` 680 y `xl` 1280, y este monitor es el `xl`.
 */
const auDialogProps = {
  maxWidth: false as const,
  PaperProps: {
    sx: {
      ...AU_DIALOG_PAPER_SX,
      width: '100%',
      maxWidth: 1280,
      // El Paper es la columna: cabecera de alto propio y cuerpo con el resto. Sin esto el
      // `DialogContent` no tiene contra qué medir su `flex: 1` y el grid de adentro no se acota.
      display: 'flex',
      flexDirection: 'column',
      /**
       * El cuerpo, **redeclarado después del spread** y no desde el `sx` del `ModalContent`.
       *
       * `AU_DIALOG_PAPER_SX` ya trae una regla para `.MuiDialogContent-root` —el padding y los
       * filos de `.au-dialog-body-content`, más un `display: grid` con `gap: 14` que sirve para un
       * diálogo de filas apiladas y no para este, que mete una grilla propia adentro—. Esa regla
       * es un descendiente (dos clases) contra la clase suelta que genera el `sx` del componente,
       * así que gana por especificidad y el `sx` del `ModalContent` se descartaría en silencio.
       * Escrito acá, mismo selector y misma especificidad, gana el que va después.
       */
      '& .MuiDialogContent-root': {
        ...AU_DIALOG_PAPER_SX['& .MuiDialogContent-root'],
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        minHeight: 0,
      },
    },
  },
  slotProps: { backdrop: { sx: AU_BACKDROP_SX } },
};

/**
 * La grilla del monitor, portada de `.oc-monitor`.
 *
 * ```
 * 'side operation'   250px │ 1fr
 * 'side notif'
 * ```
 *
 * El reparto no es decorativo: el resumen es la columna que **no cambia** durante la operación
 * —quién la pidió, a qué posición, con qué código— y por eso ocupa un borde entero y se lee una
 * vez. Todo lo que se mueve queda a la derecha, donde la vista vuelve.
 *
 * Las dos filas van **declaradas**. Con filas implícitas las dos son `auto` y pasan dos cosas a la
 * vez: el feed deja de tener tope y crece con cada mensaje, y el sobrante se reparte en partes
 * iguales entre las dos filas — que es lo que hacía que el título del feed cambiara de alto según
 * cuántas notificaciones hubiera. Con `minmax(0, 1fr)` la de abajo es el resto y deja de negociar.
 *
 * **El alto va fijado, y es lo que impide que el diálogo cambie de tamaño al terminar de cargar.**
 * Sin alto, el Paper mide su contenido: en carga no hay figura, ni gráfico, ni feed, así que el
 * modal aparecía chico y **saltaba** al llegar los datos. `height` y no `min-height` porque con un
 * mínimo la fila `1fr` de un grid de alto automático resuelve a `max-content` y el diálogo vuelve a
 * crecer con cada mensaje que entra al feed.
 *
 * Los 640 son el techo del espécimen y salen de lo que la fila de arriba no puede encoger —la
 * figura mide 170 × 230 y la barra de progreso 54— más lo que el feed y el histórico necesitan para
 * servir de algo. El `calc` lo acota contra el viewport, y los 150 son el chrome **de este**
 * diálogo, no un número copiado: 64 de margen del Paper + 48 de cabecera + 36 de padding del cuerpo
 * + 2 de bordes. Que dé el mismo número que el espécimen es coincidencia de dos diálogos con la
 * misma geometría.
 *
 * (Esto revisa la nota anterior, que invocaba G-006 para no transplantar el alto. G-006 es sobre no
 * copiar las **medidas** del mockup a ciegas; dejar el alto sin declarar no era respetar el gotcha
 * sino no resolver el problema, y el síntoma era el salto.)
 */
const MONITOR_GRID_SX = {
  display: 'grid',
  gap: '12px',
  gridTemplateColumns: '250px minmax(0, 1fr)',
  gridTemplateRows: 'auto minmax(0, 1fr)',
  gridTemplateAreas: `
"side operation"
"side notif"
`,
  height: 'min(640px, calc(100vh - 150px))',

  /**
   * Bajo 1180px la grilla **se apila en una columna** y suelta el alto fijo, igual que el
   * espécimen. Faltaba: con las dos columnas clavadas en `250px + 12 + 1fr` y 640 de alto, una
   * ventana angosta comprimía el panel de operación —que tiene la figura de 170 y la barra— contra
   * el resto, y el diálogo podía desbordar en horizontal.
   *
   * Apilado no hay fila que repartir, así que los dos que vivían de ese reparto recuperan su tope
   * propio: el feed pide 320 y el histórico un mínimo de 198. Sin eso, con `height: auto` el feed
   * crece con cada mensaje y el gráfico colapsa a cero.
   *
   * El orden cambia a `operation / side / notif`: apilado, lo primero que se lee es la operación en
   * curso, no el resumen.
   */
  '@media (max-width: 1180px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'auto auto auto',
    gridTemplateAreas: `
"operation"
"side"
"notif"
`,
    height: 'auto',
  },
} as const;

/**
 * La columna izquierda es su **propia pila** y no dos filas del grid. Con los dos paneles como
 * áreas sueltas, sus altos los decidían filas que comparten con la operación y el feed, y salía al
 * revés de lo que la lectura pide: el resumen —una lista de largo conocido— se estiraba, y el
 * histórico —que gana con cada pixel— quedaba clavado.
 */
const SIDE_SX = {
  gridArea: 'side',
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
} as const;

/** Apilado no hay alto que repartir: el histórico vuelve a pedir el suyo. */
const CHART_STACKED_SX = {
  '@media (max-width: 1180px)': { flex: 'none', minHeight: 198 },
} as const;

/** Lo mismo para el feed, que apilado recupera su tope propio en vez de vivir del `1fr`. */
const NOTIF_STACKED_SX = {
  '@media (max-width: 1180px)': { height: 320 },
} as const;

export const GateOperationModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('gateOperationModal');

  const movementId = state.open ? state.movementId : null;

  return (
    <GlobalModal
      {...globalModalProps}
      authorization={{ access: Access.Allow }}
      dialogProps={auDialogProps}
    >
      {/* Acota la suscripción de mensajes al gate de este modal (AMARU-862/US-006). No reactiva
          la telemetría de posición/batería que persigue AMARU-866 — ver el TODO del grid. */}
      <GateOperationSubscription movementId={movementId} />
      {/* La tira de conexión y batería va **dentro del título**, a su derecha: es la cabecera de la
          ventana, no un panel. `ModalTitle` ya la coloca ahí — su `children` van en el extremo
          opuesto del título, con la cruz al filo (`.oc-frame-head`). */}
      <ModalTitle
        onClose={onClose}
        title={
          /* `TruncatedTypography` explícito y no el que `ModalTitle` pone solo: sólo lo envuelve
             cuando el título es un `Trans`, un fragmento o un string, y acá es un renderer. Sin la
             clase `h6` no le llega el 14/700 de `.au-dialog-title`, que `AU_DIALOG_PAPER_SX`
             declara apuntando justo a ella. */
          <TruncatedTypography variant="h6">
            {movementId ? (
              <MovementDataRenderer
                movementId={movementId}
                render={({ gate }) => <DeviceNameSpan deviceId={gate.id} />}
                fallback={() => <Skeleton width={180} />}
              />
            ) : (
              <Trans>No hay dispositivo seleccionado</Trans>
            )}
          </TruncatedTypography>
        }
      >
        <ConnectionStatus movementId={movementId} />
      </ModalTitle>

      {/* `bottomDivider={false}`: los dos filos internos del diálogo son los `dividers` de este
          `DialogContent`, y el de abajo es el que separa del pie. Sin pie, ese filo queda como una
          línea suelta apoyada contra la esquina redondeada del Paper. */}
      <ModalContent bottomDivider={false}>
        <Box sx={MONITOR_GRID_SX}>
          <Box sx={SIDE_SX}>
            <PanelSummary movementId={movementId} />
            <PanelChart movementId={movementId} sx={CHART_STACKED_SX} />
          </Box>
          {/* TODO(AMARU-866): reactivar TelemetrySubscriber con el subscription manager on-demand. */}
          <PanelOperation movementId={movementId} />
          <PanelNotifications movementId={movementId} sx={NOTIF_STACKED_SX} />
        </Box>
      </ModalContent>
    </GlobalModal>
  );
};
